import React, { useEffect, useState } from 'react';
import {
  useGetCandidateAnswersQuery,
  useGetPreQualConfigQuery,
} from '../../../../../utils/api_service/preQualApi';
import { getVideoFromStorage } from '../../../../../utils/api_service/storage_api';

interface PreQualResultsTabProps {
  jobOpeningId: string;
  jobApplicantId: string;
  handleResendInvite: (id: string) => void;
  isResending: boolean;
  preQualSentAt?: number | string;
}

const isVideoQuestionType = (questionType?: string) =>
  questionType === 'video-answer' || questionType === 'video';

const getVideoStoragePath = (value?: string | null) => {
  if (!value) return '';

  if (!value.startsWith('http')) {
    return value.replace(/^\/+/, '');
  }

  try {
    const url = new URL(value);
    const filePath = url.searchParams.get('file_path');
    if (filePath) return filePath;
    return decodeURIComponent(url.pathname.replace(/^\/+/, ''));
  } catch {
    return value;
  }
};

export const PreQualResultsTab: React.FC<PreQualResultsTabProps> = ({
  jobOpeningId,
  jobApplicantId,
  handleResendInvite,
  isResending,
  preQualSentAt,
}) => {
  // Fetch candidate answers
  const { data: answersData, isLoading: answersLoading } =
    useGetCandidateAnswersQuery(
      {
        jobOpeningId,
        jobApplicantId,
      },
      {
        skip: !jobOpeningId || !jobApplicantId,
      },
    );

  // Fetch pre-qual configuration as fallback for question labels
  const { data: configData, isLoading: configLoading } =
    useGetPreQualConfigQuery(jobOpeningId, {
      skip: !jobOpeningId,
    });

  const isLoading = answersLoading || configLoading;
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  const [videoLoading, setVideoLoading] = useState<Record<string, boolean>>({});
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  const [selectedVideo, setSelectedVideo] = useState<{ url: string; question: string } | null>(null);

  useEffect(() => {
    const videoAnswers = answersData?.videoAnswers || [];
    if (videoAnswers.length === 0) return;

    let active = true;

    videoAnswers.forEach((videoAnswer) => {
      const questionKey = videoAnswer.questionKey;
      const filePath = getVideoStoragePath(videoAnswer.videoKey);

      if (!questionKey || !filePath || videoUrls[questionKey]) return;

      setVideoLoading((prev) => ({ ...prev, [questionKey]: true }));
      setVideoErrors((prev) => ({ ...prev, [questionKey]: false }));

      getVideoFromStorage(filePath)
        .then((url) => {
          if (!active || !url) return;
          setVideoUrls((prev) => ({ ...prev, [questionKey]: url }));
        })
        .catch(() => {
          if (!active) return;
          setVideoErrors((prev) => ({ ...prev, [questionKey]: true }));
        })
        .finally(() => {
          if (!active) return;
          setVideoLoading((prev) => ({ ...prev, [questionKey]: false }));
        });
    });

    return () => {
      active = false;
    };
  }, [answersData?.videoAnswers, videoUrls]);

  if (isLoading) {
    return (
      <div className='p-8 flex flex-col items-center justify-center min-h-[300px]'>
        <div className='w-12 h-12 border-4 border-gray-100 border-t-indigo-600 rounded-full animate-spin mb-4 shadow-sm'></div>
        <p className='text-gray-500 font-medium animate-pulse text-sm'>
          Preparing candidate profile summary...
        </p>
      </div>
    );
  }

  const answers = answersData?.answers;
  // Use config data as the primary source of truth for labels and count
  const questions = configData?.questions || answersData?.questions;

  if (!answers || !questions || questions.length === 0) {
    return (
      <div className='p-12 text-center bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-200 m-6'>
        <div className='w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100'>
          <svg
              className='w-10 h-10 text-gray-300'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-bold text-gray-900 mb-2 tracking-tight'>
          Incomplete Records
        </h3>
        <p className='text-gray-500 text-sm max-w-[240px] mx-auto mb-8'>
          We couldn&apos;t find any pre-qualification data for this candidate
          yet.
        </p>

        <button
            onClick={() => handleResendInvite(jobApplicantId)}
            disabled={isResending}
            className='group bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-purple-400 disabled:to-pink-400 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 mx-auto ring-4 ring-purple-50'
            aria-label='Resend pre-qualification invite'
        >
          { isResending ? (
            <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
          ) : (
            <svg
                className='w-4 h-4 transition-transform duration-200 group-hover:scale-110'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
          ) }
          <span className='tracking-tight'>
            { isResending ? 'Sending Invitation...' : 'Send Pre Qualifying Invite' }
          </span>
        </button>
        { preQualSentAt && (
          <p className='mt-4 text-xs font-bold text-emerald-600 uppercase tracking-wider animate-pulse'>
            Pre Qualification Invite sent on: { typeof preQualSentAt === 'number' ? new Date(preQualSentAt * 1000).toLocaleString() : new Date(preQualSentAt).toLocaleString() }
          </p>
        ) }
      </div>
    );
  }

  // Format the submission date nicely
  const formattedDate = (() => {
    const dateVal = answers.submittedAt;
    if (!dateVal) return 'N/A';
    const date =
      typeof dateVal === 'number' && dateVal < 10000000000
        ? new Date(dateVal * 1000)
        : new Date(dateVal);

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  })();

  const handleCloseVideoModal = () => {
    setSelectedVideo(null);
  };

  const VideoModal = () => {
    if (!selectedVideo) return null;

    return (
      <div className='fixed inset-0 z-50 overflow-y-auto'>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          { /* Background overlay */ }
          <div
              className='fixed inset-0 bg-slate-900 bg-opacity-60 backdrop-blur-sm transition-opacity'
              onClick={handleCloseVideoModal}
          ></div>

          { /* Modal panel */ }
          <div className='inline-block align-bottom bg-white rounded-3xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 border border-slate-100'>
            <div className='absolute top-0 right-0 pt-4 pr-4'>
              <button
                  type='button'
                  className='bg-white rounded-xl p-1.5 text-slate-400 hover:text-slate-600 focus:outline-none transition-colors border border-slate-100 hover:border-slate-200'
                  onClick={handleCloseVideoModal}
              >
                <span className='sr-only'>Close</span>
                <svg
                    className='h-5 w-5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2.5}
                      d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='sm:flex sm:items-start'>
              <div className='w-full'>
                { /* Modal Header */ }
                <div className='flex items-center gap-3 mb-6'>
                  <div className='w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0 shadow-sm'>
                    <svg
                        className='w-5 h-5 text-emerald-600'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className='text-sm font-bold text-slate-800 tracking-tight'>
                      Video Response
                    </h3>
                    <p className='text-xs text-slate-500 mt-0.5 font-semibold leading-relaxed max-w-[90%]'>
                      { selectedVideo.question }
                    </p>
                  </div>
                </div>

                { /* Video Player */ }
                <div className='bg-slate-950 rounded-2xl overflow-hidden shadow-inner border border-slate-900 aspect-video flex items-center justify-center w-full'>
                  <video
                      className='w-full h-full'
                      src={selectedVideo.url}
                      preload='metadata'
                      controls
                      autoPlay
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <VideoModal />
      <div className='p-4 space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-700'>
        { /* High-Level Profile Summary Header */ }
        <div className='flex items-center justify-between bg-white px-5 py-4 rounded-2xl shadow-sm border border-slate-100'>
          <div className='flex items-center gap-4'>
            <div className='w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center border border-emerald-100 flex-shrink-0'>
              <svg
                  className='w-5 h-5 text-emerald-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div>
              <h3 className='text-sm font-bold text-slate-800 tracking-tight'>
                Questionnaire Profile
              </h3>
              <div className='flex items-center gap-2 mt-0.5'>
                <span className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse' />
                <p className='text-[11px] text-slate-400 font-medium'>
                  Completed on { formattedDate }
                </p>
              </div>
            </div>
          </div>
          <div className='hidden sm:block text-[10px] font-bold text-slate-300 uppercase tracking-widest'>
            verified applicant
          </div>
        </div>

        { /* Information Grid - Professional Tiles */ }
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
          { [...questions]
            .sort((a, b) => a.order - b.order)
            .map((q, idx) => {
              const value = (() => {
                // Priority 1: Direct key mapping
                let rawVal = answers[q.questionKey || ''];

                // Priority 2: Case-insensitive and dash-to-camel mapping (e.g. current_salary -> currentSalary)
                if (rawVal === undefined && q.questionKey) {
                  const camelKey = q.questionKey
                    .toLowerCase()
                    .replace(/([-_][a-z])/g, (group) =>
                      group.toUpperCase().replace('-', '').replace('_', ''),
                    );

                  const targetKey = q.questionKey.toLowerCase();
                  const foundKey = Object.keys(answers).find(
                    (k) =>
                      k.toLowerCase() === targetKey ||
                      k.toLowerCase() === camelKey.toLowerCase(),
                  );
                  if (foundKey) rawVal = answers[foundKey];
                }

                // Priority 3: Boolean formatting
                if (typeof rawVal === 'boolean') return rawVal ? 'Yes' : 'No';

                // Priority 4: Location data mapping
                if (isVideoQuestionType(q.questionType)) {
                  const videoAnswer = answersData?.videoAnswers?.find(
                    (va) =>
                      va.questionKey &&
                      q.questionKey &&
                      va.questionKey.toLowerCase() ===
                        q.questionKey.toLowerCase(),
                  );
                  const rawVideoAnswer = q.questionKey
                    ? answersData?.answers?.[q.questionKey]
                    : null;
                  const rawVideoKey =
                    typeof rawVideoAnswer === 'string'
                      ? rawVideoAnswer
                      : rawVideoAnswer?.videoKey ||
                        rawVideoAnswer?.url ||
                        rawVideoAnswer?.value;

                  return (
                    getVideoStoragePath(
                      videoAnswer?.videoKey || videoAnswer?.url,
                    ) ||
                    getVideoStoragePath(rawVideoKey) ||
                    rawVal ||
                    null
                  );
                }

                // Priority 5: Location data mapping
                if (q.questionType === 'location') {
                  const locAns = answersData?.locationAnswers?.find(
                    (la) =>
                      la.questionKey &&
                      q.questionKey &&
                      la.questionKey.toLowerCase() ===
                        q.questionKey.toLowerCase(),
                  );
                  if (locAns) {
                    return `${locAns.city}${
                      locAns.state ? `, ${locAns.state}` : ''
                    }, ${locAns.country}`;
                  }
                }

                return rawVal !== undefined ? String(rawVal) : null;
              })();

              if (!value) return null;

              return (
                <div
                    key={q.questionKey || idx}
                    className={`group bg-white p-4 rounded-2xl border border-slate-100 hover:border-emerald-200 transition-all duration-300 hover:shadow-md ${
                      isVideoQuestionType(q.questionType)
                        ? 'sm:col-span-2 lg:col-span-2'
                        : ''
                    }`}
                >
                  <div className='flex flex-col gap-1.5'>
                    <div className='flex items-center justify-between mb-1'>
                      <span className='text-[10px] font-black text-slate-300 uppercase tracking-wider group-hover:text-emerald-500 transition-colors'>
                        { (idx + 1).toString().padStart(2, '0') }.{ ' ' }
                        { q.questionType.replace('-', ' ') }
                      </span>
                      { q.questionType === 'location' && (
                        <svg
                            className='w-3 h-3 text-slate-300'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                        >
                          <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                          />
                        </svg>
                      ) }
                    </div>
                    <h4 className='text-[10px] font-bold text-slate-500 uppercase leading-tight'>
                      { q.question }
                    </h4>
                    { isVideoQuestionType(q.questionType) ? (
                      <div className='mt-3'>
                        { videoUrls[q.questionKey || ''] ? (
                          <div className='flex flex-col gap-2'>
                            <button
                                onClick={() => setSelectedVideo({ url: videoUrls[q.questionKey || ''], question: q.question })}
                                className='inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-2xl hover:bg-emerald-100 active:bg-emerald-200 transition-colors shadow-sm w-full sm:w-auto'
                            >
                              <svg
                                  className='w-4 h-4 text-emerald-600'
                                  fill='none'
                                  viewBox='0 0 24 24'
                                  stroke='currentColor'
                              >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                                />
                              </svg>
                              View Live Response
                            </button>
                          </div>
                        ) : (
                          <div
                              className={`rounded-2xl border p-4 ${
                                videoErrors[q.questionKey || '']
                                  ? 'border-rose-100 bg-rose-50'
                                  : 'border-emerald-100 bg-emerald-50/50'
                              }`}
                          >
                            <div className='flex items-start gap-3'>
                              <div className='w-8 h-8 rounded-xl bg-white flex items-center justify-center border border-emerald-100 flex-shrink-0'>
                                <svg
                                    className={`w-4 h-4 ${
                                      videoErrors[q.questionKey || '']
                                        ? 'text-rose-600'
                                        : 'text-emerald-600'
                                    }`}
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                  { videoErrors[q.questionKey || ''] ? (
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
                                  ) : (
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                                    />
                                  ) }
                                </svg>
                              </div>
                              <div className='min-w-0'>
                                <p
                                    className={`text-[13px] font-bold ${
                                      videoErrors[q.questionKey || '']
                                        ? 'text-rose-700'
                                        : 'text-emerald-700'
                                    }`}
                                >
                                  { videoErrors[q.questionKey || '']
                                    ? 'Video could not be loaded'
                                    : videoLoading[q.questionKey || '']
                                      ? 'Loading video response...'
                                      : 'Video response saved' }
                                </p>
                                <p className='mt-1 text-[10px] font-mono text-emerald-700/70 break-all'>
                                  { value }
                                </p>
                              </div>
                            </div>
                          </div>
                        ) }
                      </div>
                    ) : (
                      <p className='text-[13px] font-bold text-slate-800 tracking-tight leading-snug break-words mt-1'>
                        { value }
                      </p>
                    ) }
                  </div>
                </div>
              );
            }) }
        </div>

        { /* Signature Footer - Professional Stamp */ }
        <div className='pt-6 pb-2 border-t border-slate-50 flex items-center justify-center gap-6'>
          <div className='flex items-center gap-1.5 opacity-30 grayscale'>
            <span className='text-[8px] font-black tracking-[0.2em] text-slate-400 uppercase'>
              Security Verified
            </span>
            <div className='w-1 h-1 rounded-full bg-slate-300' />
            <span className='text-[8px] font-black tracking-[0.2em] text-slate-400 uppercase'>
              HiAcuity IntelliGate
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
